/**
 * AdminCustomersTab — Klanten cockpit voor Admin (Iter 78).
 *
 * Toont alle betalende klanten uit `/api/admin/customers` met payment,
 * account, onboarding, devices en P1-status. Klik op een rij opent een
 * detailkaart met volledige info en owner-acties:
 *   - Bekijken (default)
 *   - Accountlink opnieuw aanmaken / sturen
 *   - Later
 *   - Markeer als gecontroleerd (lokaal)
 *
 * Geen volledige PII: e-mail blijft gemaskeerd (server returnt `email_masked`).
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Users, Loader2, RefreshCw, CheckCircle2, Clock, AlertCircle, Eye, X, Copy, Mail, Activity, Package } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' }); } catch { return iso; }
}

function Badge({ tone = 'slate', children, testId }) {
  const cls = {
    emerald: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30',
    sky: 'bg-sky-500/15 text-sky-300 border-sky-400/30',
    amber: 'bg-amber-500/15 text-amber-300 border-amber-400/30',
    rose: 'bg-rose-500/15 text-rose-300 border-rose-400/30',
    slate: 'bg-white/5 text-white/70 border-white/15',
  }[tone];
  return <span data-testid={testId} className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border font-medium ${cls}`}>{children}</span>;
}

function paymentTone(s) { return s === 'active' ? 'emerald' : s === 'cancelled' ? 'rose' : 'amber'; }
function accountTone(passwordSet, status) {
  if (passwordSet) return 'emerald';
  if (status === 'password_unset') return 'amber';
  return 'slate';
}
function p1Tone(s) { return s === 'gekoppeld' ? 'emerald' : s === 'voorbereid' ? 'sky' : 'slate'; }

export default function AdminCustomersTab({ token }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [filter, setFilter] = useState('all');
  const [openId, setOpenId] = useState(null);
  const [reviewed, setReviewed] = useState({});

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const res = await axios.get(`${API}/admin/customers`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(res.data?.items || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Kon klanten niet laden.');
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => {
    load();
    try { const r = JSON.parse(localStorage.getItem('powersure_admin_customers_reviewed') || '{}'); setReviewed(r); } catch { /* ignore */ }
  }, [load]);

  const toggleReviewed = (uid) => {
    const next = { ...reviewed, [uid]: !reviewed[uid] };
    setReviewed(next);
    try { localStorage.setItem('powersure_admin_customers_reviewed', JSON.stringify(next)); } catch { /* ignore */ }
  };

  const filtered = items.filter((it) => {
    if (filter === 'all') return true;
    if (filter === 'unset_password') return !it.password_set;
    if (filter === 'onboarding_open') return !it.onboarding?.completed;
    if (filter === 'no_devices') return (it.devices_added || 0) === 0;
    return true;
  });

  return (
    <div className="space-y-4" data-testid="admin-customers-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.2em] text-emerald-300/90 font-semibold">Klanten cockpit</div>
          <h2 className="text-xl font-semibold text-white" style={{ fontFamily: 'Outfit' }}>Betalende klanten</h2>
          <p className="text-xs text-white/65 mt-1">Payment · account · onboarding · apparaten · P1-status. E-mails zijn gemaskeerd.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="bg-white/5 border border-white/15 rounded-md px-2 py-1.5 text-sm text-white/85" data-testid="admin-customers-filter">
            <option value="all">Alle</option>
            <option value="unset_password">Zonder wachtwoord</option>
            <option value="onboarding_open">Onboarding open</option>
            <option value="no_devices">Zonder apparaten</option>
          </select>
          <button onClick={load} className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-white/15 text-white/80 hover:bg-white/10" data-testid="admin-customers-reload">
            <RefreshCw className="w-3 h-3" /> Vernieuwen
          </button>
        </div>
      </div>

      {err && <div className="rounded-md border border-rose-400/30 bg-rose-500/10 text-rose-200 text-sm px-3 py-2 inline-flex items-center gap-2"><AlertCircle className="w-4 h-4" /> {err}</div>}
      {loading ? (
        <div className="text-white/60 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Laden…</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-md border border-white/10 bg-white/5 p-4 text-sm text-white/65">Geen klanten in deze filter.</div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-white/10" data-testid="admin-customers-table">
          <table className="min-w-full text-sm">
            <thead className="bg-white/5 text-[11px] uppercase tracking-wider text-white/55">
              <tr>
                <th className="text-left px-3 py-2">Klant</th>
                <th className="text-left px-3 py-2">Payment</th>
                <th className="text-left px-3 py-2">Account</th>
                <th className="text-left px-3 py-2">Onboarding</th>
                <th className="text-left px-3 py-2">Apparaten</th>
                <th className="text-left px-3 py-2">P1</th>
                <th className="text-left px-3 py-2">Actie</th>
              </tr>
            </thead>
            <tbody className="text-white/85">
              {filtered.map((it) => (
                <tr key={it.user_id} className="border-t border-white/5 hover:bg-white/3" data-testid={`admin-customers-row-${it.user_id}`}>
                  <td className="px-3 py-2">
                    <div className="text-sm">{it.email_masked || '—'}</div>
                    <div className="text-[10px] text-white/45">{fmtDate(it.created_at)}{reviewed[it.user_id] ? ' · ✓ gecontroleerd' : ''}</div>
                  </td>
                  <td className="px-3 py-2"><Badge tone={paymentTone(it.payment?.status)}>{it.payment?.status || '—'}</Badge></td>
                  <td className="px-3 py-2">
                    <Badge tone={accountTone(it.password_set, it.account_status)}>{it.password_set ? 'Wachtwoord OK' : 'Geen wachtwoord'}</Badge>
                    {!it.password_set && (
                      <div className="text-[10px] text-white/55 mt-0.5">
                        mail: {it.last_account_mail_status || it.payment?.welcome_mail_status || '—'}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2"><Badge tone={it.onboarding?.completed ? 'emerald' : 'amber'}>{it.onboarding?.completed ? 'Afgerond' : 'Open'}</Badge></td>
                  <td className="px-3 py-2"><span className="text-sm">{it.devices_added ?? 0}</span></td>
                  <td className="px-3 py-2"><Badge tone={p1Tone(it.p1_status)}>{it.p1_status || 'niet_gekoppeld'}</Badge></td>
                  <td className="px-3 py-2">
                    <button onClick={() => setOpenId(it.user_id)} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-white/15 text-white/85 hover:bg-white/10" data-testid={`admin-customers-view-${it.user_id}`}>
                      <Eye className="w-3 h-3" /> Bekijken
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openId && <CustomerDetailModal token={token} userId={openId} onClose={() => setOpenId(null)} onMarkReviewed={() => toggleReviewed(openId)} reviewed={!!reviewed[openId]} />}
    </div>
  );
}

function CustomerDetailModal({ token, userId, onClose, onMarkReviewed, reviewed }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [link, setLink] = useState('');
  const [linkBusy, setLinkBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/admin/customers/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
        if (active) setData(r.data);
      } catch (e) {
        if (active) setErr(e?.response?.data?.detail || 'Kon klantdetail niet laden.');
      } finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [userId, token]);

  const mintLink = async () => {
    setLink(''); setLinkBusy(true); setErr(''); setCopied(false);
    try {
      const r = await axios.post(`${API}/admin/customers/${userId}/set-password-link`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setLink(r.data?.url || '');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Mint mislukt.');
    } finally { setLinkBusy(false); }
  };

  const [mailBusy, setMailBusy] = useState(false);
  const [mailResult, setMailResult] = useState(null);
  const sendMail = async () => {
    setMailBusy(true); setErr(''); setMailResult(null);
    try {
      const r = await axios.post(`${API}/admin/customers/${userId}/send-account-mail`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setMailResult(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Mail-actie mislukt.');
    } finally { setMailBusy(false); }
  };

  const copyLink = async () => {
    if (!link) return;
    try { await navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch { /* ignore */ }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-3 sm:p-6 bg-black/70 backdrop-blur-sm" role="dialog" data-testid="admin-customer-detail-overlay">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border border-white/10 p-5 sm:p-6 relative" style={{ background: 'linear-gradient(180deg, #0e1b16 0%, #0a1410 100%)' }} data-testid="admin-customer-detail-sheet">
        <button onClick={onClose} aria-label="Sluiten" className="absolute top-3 right-3 text-white/55 hover:text-emerald-300" data-testid="admin-customer-detail-close"><X className="w-5 h-5" /></button>
        <h3 className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit' }}>Klantdetail</h3>
        {loading ? (
          <div className="text-white/60 mt-2 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Laden…</div>
        ) : err ? (
          <div className="text-rose-300 text-sm mt-2 inline-flex items-center gap-2"><AlertCircle className="w-4 h-4" /> {err}</div>
        ) : data ? (
          <div className="mt-3 space-y-3 text-sm text-white/85">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-white/55">Identiteit</div>
              <div className="text-sm text-white">{data.email_masked}</div>
              <div className="text-[11px] text-white/55">Aangemaakt {fmtDate(data.created_at)} · laatste login {fmtDate(data.last_login_at)} · {data.password_set ? <span className="text-emerald-300">wachtwoord ingesteld</span> : <span className="text-amber-300">wachtwoord nog niet ingesteld</span>}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-white/55">Onboarding</div>
              {data.setup_completed ? (
                <div className="grid grid-cols-2 gap-2 text-[12px] mt-1">
                  <div className="rounded bg-white/5 border border-white/10 p-2"><div className="text-white/55 text-[10px]">Doel</div><div>{data.onboarding?.goal || '—'}</div></div>
                  <div className="rounded bg-white/5 border border-white/10 p-2"><div className="text-white/55 text-[10px]">Zon</div><div>{data.onboarding?.has_solar || '—'}</div></div>
                  <div className="rounded bg-white/5 border border-white/10 p-2"><div className="text-white/55 text-[10px]">P1</div><div>{data.onboarding?.has_p1 || '—'}</div></div>
                  <div className="rounded bg-white/5 border border-white/10 p-2"><div className="text-white/55 text-[10px]">Sturing</div><div>{data.onboarding?.control_preference || '—'}</div></div>
                </div>
              ) : <div className="text-amber-300 text-[12px]">Onboarding nog niet afgerond.</div>}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-white/55">P1 / meetbron</div>
              <div className="text-sm">{(data.p1?.kind) ? `${data.p1.kind} — ${data.p1.status}` : 'Nog niet gekozen'}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-white/55 mb-1">Apparaten ({data.devices?.length || 0})</div>
              {(data.devices || []).length === 0 ? (
                <div className="text-white/60 text-[12px]">Geen apparaten toegevoegd.</div>
              ) : (
                <ul className="space-y-1">
                  {data.devices.map((d) => (
                    <li key={d.id} className="rounded bg-white/5 border border-white/10 px-2 py-1.5 flex items-center justify-between gap-2 text-[12px]" data-testid={`admin-customer-device-${d.id}`}>
                      <span className="truncate"><Package className="w-3 h-3 inline mr-1" /> {d.category} · {d.brand || ''} {d.model || ''}</span>
                      <Badge tone="slate">{d.source || 'manual'}</Badge>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="pt-2 border-t border-white/10">
              <div className="text-[10px] uppercase tracking-wider text-white/55 mb-2">Owner-acties</div>
              <div className="flex flex-wrap gap-2">
                <button onClick={sendMail} disabled={mailBusy} className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md bg-emerald-500 text-emerald-950 font-semibold disabled:opacity-60" data-testid="admin-customer-send-mail">
                  {mailBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
                  Accountmail versturen
                </button>
                <button onClick={mintLink} disabled={linkBusy} className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-emerald-400/30 bg-emerald-500/10 text-emerald-200 font-medium disabled:opacity-60" data-testid="admin-customer-mint-link">
                  {linkBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Copy className="w-3 h-3" />}
                  Accountlink opnieuw aanmaken
                </button>
                <button onClick={onMarkReviewed} className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-white/15 text-white/85 hover:bg-white/10" data-testid="admin-customer-mark-reviewed">
                  <CheckCircle2 className="w-3 h-3" /> {reviewed ? 'Markering opheffen' : 'Markeer als gecontroleerd'}
                </button>
                <button onClick={onClose} className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-white/15 text-white/85 hover:bg-white/10" data-testid="admin-customer-later">
                  <Clock className="w-3 h-3" /> Later
                </button>
              </div>
              {mailResult && (
                <div className={`mt-3 rounded-md border px-3 py-2 text-[12px] ${mailResult.dry_run ? 'bg-amber-500/10 border-amber-400/30 text-amber-200' : 'bg-emerald-500/10 border-emerald-400/30 text-emerald-200'}`} data-testid="admin-customer-send-mail-result">
                  Status: <strong>{mailResult.status || 'onbekend'}</strong>{mailResult.dry_run ? ' — EMAIL_DRY_RUN staat aan; zet env-var op false om écht te versturen.' : ' — verstuurd via Resend.'}
                  {mailResult.error ? <div className="text-rose-200 mt-1">Fout: {mailResult.error}</div> : null}
                </div>
              )}
              {link && (
                <div className="mt-3 rounded-md bg-emerald-500/10 border border-emerald-400/30 px-3 py-2" data-testid="admin-customer-mint-link-result">
                  <div className="text-[11px] uppercase tracking-wider text-emerald-300 mb-1">Eenmalige link (NIET delen via onbeveiligde kanalen)</div>
                  <div className="flex items-center gap-2">
                    <input readOnly value={link} className="flex-1 bg-black/30 text-[12px] text-white/85 px-2 py-1.5 rounded border border-white/10" />
                    <button onClick={copyLink} className="inline-flex items-center gap-1 text-xs px-2 py-1.5 rounded-md border border-white/15 text-white/85" data-testid="admin-customer-mint-link-copy">
                      <Copy className="w-3 h-3" /> {copied ? 'Gekopieerd' : 'Kopieer'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
